import { useSyncExternalStore } from 'react';
import { ladeEingeklappt, speichereEingeklappt } from './eingeklappt';

/**
 * „Ist die Seitenleiste schmal?" - an EINER Stelle.
 *
 * ═══ WARUM DAS EINE FRAGE FUER MEHRERE STELLEN IST ══════════════════════
 *
 * Bis SL6 wusste nur `Sidebar.tsx`, ob die Leiste schmal ist: ein
 * `useState` fuer den Einklappwunsch und ein zweites fuer die
 * Fensterbreite. Seit SL7 haengt eine zweite Entscheidung daran - der
 * innere Gliederungsbaum des Gewerks Elektro (`ElektroRahmen.tsx`) wird
 * NUR gezeigt, wenn die Leiste schmal ist; sonst stuende derselbe Baum
 * zweimal im Bild.
 *
 * Ein zweiter Nachbau derselben Bedingung in `ElektroRahmen` waere genau
 * der Fehler, den dieses Paket sonst beseitigt hat: zwei Stellen, die
 * dasselbe behaupten, und eines Tages behaupten sie Verschiedenes. Der
 * Zustand liegt deshalb hier, und beide lesen ihn.
 *
 * ═══ WARUM EIN KLEINER SPEICHER UND KEIN CONTEXT ════════════════════════
 *
 * `ElektroRahmen` liegt im Outlet-Baum unter `AppShell`, aber nicht unter
 * `Sidebar`. Ein Context muesste also in `AppShell` haengen - und
 * `AppShell` ist die Layoutroute, die am 17.09.2026 die Produktion weiss
 * gemacht hat, weil eine Ebene mehr im Router den Outlet-Kontext
 * abschnitt. Dort nichts anzufassen ist billiger als jede Vorsicht.
 *
 * `useSyncExternalStore` braucht keinen Anbieter, kein Umhaengen im
 * Router und keine zusaetzliche Ebene. Wer den Wert liest, bekommt ihn -
 * gleich wo er im Baum steht.
 *
 * Der Einklappwunsch ueberlebt in `localStorage` (siehe `eingeklappt.ts`),
 * die Fensterbreite wird bei jedem Lesen frisch gemessen; das `resize`-
 * Ereignis meldet nur, dass neu zu lesen ist.
 */

/** Ab hier klappt die Leiste selbsttaetig ein (Bedienung bei 1366×768). */
export const SCHMAL_AB_PX = 1280;

let eingeklappt = ladeEingeklappt();

const hoerer = new Set<() => void>();

function melden(): void {
  for (const h of hoerer) h();
}

function abonnieren(hoerend: () => void): () => void {
  hoerer.add(hoerend);
  if (hoerer.size === 1 && typeof window !== 'undefined') {
    window.addEventListener('resize', melden);
  }
  return () => {
    hoerer.delete(hoerend);
    if (hoerer.size === 0 && typeof window !== 'undefined') {
      window.removeEventListener('resize', melden);
    }
  };
}

function engesFenster(): boolean {
  return typeof window !== 'undefined' && window.innerWidth < SCHMAL_AB_PX;
}

/*
 * Beide Schnappschuesse geben einen BOOLEAN zurueck, keinen frisch
 * gebauten Gegenstand: `useSyncExternalStore` vergleicht mit `Object.is`
 * und geriete bei einem neuen Objekt je Aufruf in eine Endlosschleife.
 */
function schmalJetzt(): boolean {
  return eingeklappt || engesFenster();
}

function eingeklapptJetzt(): boolean {
  return eingeklappt;
}

/** Ohne Fenster (Server-Rendering) gilt: ausgeklappt. */
function beimServer(): boolean {
  return false;
}

/** Ist die Leiste schmal - von Hand eingeklappt ODER enges Fenster? */
export function useSchmaleLeiste(): boolean {
  return useSyncExternalStore(abonnieren, schmalJetzt, beimServer);
}

/**
 * Nur der Wunsch des Benutzers, ohne die Fensterbreite. Das ist NICHT
 * dasselbe: Wird das Fenster wieder breit, gilt wieder, was er eingestellt
 * hat - deshalb darf die Fensterbreite seinen Wunsch nicht ueberschreiben.
 */
export function useEingeklappt(): boolean {
  return useSyncExternalStore(abonnieren, eingeklapptJetzt, beimServer);
}

/** Den Einklappwunsch setzen (und fuer das naechste Mal merken). */
export function setzeEingeklappt(wert: boolean): void {
  if (eingeklappt === wert) return;
  eingeklappt = wert;
  speichereEingeklappt(wert);
  melden();
}
